/*
 * View for the intent example editor
 */

define([
  "marionette",
  "jquery",
  "messaging/messenger",
  "rangyinputs",
  "modules/topicsSplit/topic/topicExampleModal/exampleEditor/template/exampleEditorTemplate.html",
  "modules/topicsSplit/topic/topicExampleModal/exampleEditor/template/exampleEditorEntryTemplate.html",
], function (
  Marionette,
  $,
  Messenger,
  rangyinputs,
  ExampleEditorTemplate,
  ExampleEditorEntryTemplate,
) {
  let ExampleEditorView = {};

  ExampleEditorView.exampleEditorEntry = Marionette.ItemView.extend({
    tagName: "span",
    template: ExampleEditorEntryTemplate,
    className: function () {
      if (this.model.get("isOptional")) return "optional";
      if (this.model.get("substitutionGroupId") && this.model.get("substitutionGroupId") > 0) {
        if (!this.model.get("substitutionBegin")) {
          //part of a multiword sub group (and not the first word), so add class to extend the blue line to the previous word
          return "sub sub-multi-word";
        } else {
          return "sub";
        }
      }
    },
    events: {
      //set as an event instead of a trigger so that the event will propogate
      "click .dropdown-toggle": function () {
        this.trigger("click:word");
        return true;
      },
    },
    modelEvents: {
      change: "_renderAndResetClass",
    },
    _renderAndResetClass: function () {
      this.render();
      this.resetClass();
    },
    resetClass: function () {
      this.$el.removeClass(); //remove all existing classes
      this.$el.addClass(this.className()); //add back the correct class
    },
    setLoadingSpinner: function () {
      //clear out the dropdown menu and insert a loading spinner
      this.$el.find(".dropdown-menu").empty();
      this.$el.find(".dropdown-menu").html('<li><i class="icon-spinner icon-spin"></i></li>');
    },
    setSubChoices: function (subGroups) {
      //save subgroups on view
      this.subGroups = subGroups;
      //insert menu items into the dropdown menu
      const $menu = this.$el.find(".dropdown-menu");

      //but first, clear out the dropdown menu (removing the loading spinner)
      $menu.empty();

      //if there are no sub groups, show the "no sub group" message
      if (subGroups.length === 0) {
        $menu.append(
          "<li class=\"disabled\"><a href='#'>" + $.t("topic.example.noSubGroups") + "</a></li>",
        );
        return;
      }

      //create html strings for the components of the menu: the current sub group and the sub group choices
      //we will build these up in this function
      let currentGroupMenu = "";
      let groupChoicesMenu = "";

      //if there is more than one sub group and a sub group is selected, show the "change substitution" menu title
      //or if there is at least one sub group and a sub group is not selected, show the "add substitution" menu title
      if (subGroups.length > 1 && this.model.get("substitutionGroupId") > 0) {
        groupChoicesMenu +=
          '<li id="subAction" class="menu-label">' + $.t("topic.example.subChange") + "</li>";
      }
      if (subGroups.length > 0 && this.model.get("substitutionGroupId") === 0) {
        groupChoicesMenu +=
          '<li id="subAction" class="menu-label">' + $.t("topic.example.subAdd") + "</li>";
      }

      //add the available sub groups to the list
      subGroups.each(function (groupMatch) {
        //if the group is currently the sub group for this word, add the name to the currentSub dropdown title
        if (
          this.model.get("substitutionGroupId") === groupMatch.get("substitutionGroupId") &&
          this.model.isCurrentSubGroup(groupMatch)
        ) {
          //we need to do this check because a single sub group may match on multiple entries
          let groupMatchString = groupMatch.get("substitutionGroupName");
          if (groupMatch.get("multiWord")) {
            groupMatchString += " (" + groupMatch.get("matchingEntry") + ")";
          }
          currentGroupMenu +=
            '<li class="menu-label" id="currentSub">' + groupMatchString + "</li>";
          //if the group match is auto applied, then insert the auto applied message
          if (groupMatch.get("autoApplied")) {
            currentGroupMenu +=
              "<li class='disabled'><a href='#'><span class='muted'>" +
              $.t("topic.example.autoApplied") +
              "</span></a></li>";
          } else {
            //add remove option to the menu
            currentGroupMenu +=
              '<li id="removeSub"><a href="#" class="shallow-indent"><i class="icon-remove text-error"></i>' +
              $.t("common.remove") +
              "</a></li>";
          }
        } else {
          //add sub group to sub group options list
          let groupHTML =
            '<li class="sub-option" id="' +
            groupMatch.get("substitutionGroupId") +
            '" name="' +
            groupMatch.get("matchingEntry") +
            '"><a title="' +
            groupMatch.get("description") +
            '">' +
            groupMatch.get("substitutionGroupName");
          if (groupMatch.get("multiWord")) {
            groupHTML += " (" + groupMatch.get("matchingEntry") + ")";
          }
          groupHTML += "</a></li>";
          groupChoicesMenu += groupHTML;
        }
      }, this);

      //add menu string to menu
      $menu.append(currentGroupMenu);
      $menu.append(groupChoicesMenu);

      //handle selecting a substitution group from the menu
      const _this = this;
      $menu.find("li.sub-option").on("click", function () {
        const subId = $(this).attr("id");
        const entryName = $(this).attr("name");
        _this.trigger("change:sub", subId, entryName);
      });
      //ignore clicks on disabled menu items
      $menu.find("li.disabled").on("click", function (e) {
        e.preventDefault();
      });
    },
    triggers: {
      "click #removeSub > a": "remove:sub",
    },
  });

  ExampleEditorView.exampleEditorView = Marionette.CompositeView.extend({
    itemView: ExampleEditorView.exampleEditorEntry,
    itemViewContainer: ".editor-underlay",
    template: ExampleEditorTemplate,
    //override default implementation and insert a space between the itemViews
    appendHtml: function (collectionView, itemView, index) {
      const $container = this.getItemViewContainer(collectionView);
      if (index > 0) {
        const spaceSpan = " ";
        $container.append(spaceSpan); //insert space
      }
      $container.append(itemView.el);
    },
    collectionEvents: {
      sort: "_renderChildren",
    },
    getCursorPosition: function () {
      return this.$el.find("textarea").getSelection().end;
    },
    getTextArea: function () {
      return this.$el.find("textarea").val();
    },
    hideButtons: function (isHidden) {
      // Hide the optional and substitutions buttons when a locale other than English is selected
      if (isHidden) {
        this.$el.find("#optionalButton").hide();
        this.$el.find("#subButton").hide();
      } else {
        this.$el.find("#optionalButton").show();
        this.$el.find("#subButton").show();
      }
    },
    hideTranslateButton: function (isHidden) {
      // Hide the translate button when an english locale is selected
      if (isHidden) {
        this.$el.find("#translateButton").hide();
      } else {
        this.$el.find("#translateButton").show();
      }
    },
    events: {
      //need to use events instead of triggers so that the click event is propegated to close any dropdowns
      "click #optionalButton": function () {
        this.trigger("optional:mode");
      },
      "click #subButton": function () {
        this.trigger("sub:mode");
      },
      "click #translateButton": function () {
        this.trigger("translate");
      },
      "click textarea": function () {
        //IE Fix: The cursor may not be placed in the textarea after viewing the response editor.
        //This can be seen when the cursor position ends at position 0.  If this occurs, manually set the cursor position
        if (this.$el.find("textarea").getSelection().end === 0) {
          this.$el.find("textarea").setSelection(0, 0);
        }
      },
    },
    hideTextArea: function () {
      this.$el.find("textarea").hide();
      //also set the color of the underlay spans black so they are now visible
      this.$el.find(".editor-underlay").addClass("underlay-active");
    },
    onRender: function () {
      this.$el.i18n();
      //set textarea text
      this.collection.onSynced(function () {
        this._setTextArea();
      }, this);
      const _this = this;
      const $textarea = this.$el.find("textarea");
      //block line breaks
      $textarea.on("keypress", function (e) {
        if (e.which == 13) {
          e.preventDefault();
        }
      });
      const textarea = $textarea.get(0);
      //if ie (even ie10, even though it would work with the oninput method, so 'guilt by association' for now...)
      if (textarea.attachEvent) {
        textarea.attachEvent("onpropertychange", function (e) {
          if (e.propertyName === "value") {
            console.log("IE property change");
            _this.updateText();
          }
        });
        $(document).on("selectionchange", function () {
          console.log("IE selection change");
          _this.updateText();
        });
      } else {
        $textarea.on("input", function () {
          console.log("html5 input change");
          _this.updateText();
        });
      }
    },
    processWhitespace: function (text) {
      let newText = text.replace(/[\r\n]/g, " "); //replace line breaks with spaces
      newText = newText.replace(/\s{2,}/g, " "); //replace 2 or more spaces with a single space
      return newText;
    },
    showTextArea: function () {
      this.$el.find("textarea").show();
      //reset the color of the underlay spans to white
      this.$el.find(".editor-underlay").removeClass("underlay-active");
    },
    toggleOptionalMode: function (optionalMode) {
      if (optionalMode) {
        this.$el.find("#optionalButton").addClass("active");
        //for each child, disable words with sub groups (any other word will get it's optional attribute toggled and trigger a render, preventing the dropdown from appearing)
        this.children.each(function (itemView) {
          //disable
          if (itemView.model.get("substitutionGroupId") > 0) {
            itemView.$el.find(".dropdown-toggle").addClass("disabled");
          }
        });
      } else {
        this.$el.find("#optionalButton").removeClass("active");
      }
    },
    toggleSubMode: function (subMode) {
      if (subMode) {
        this.$el.find("#subButton").addClass("active");
        //for each child, disable the subgroup dropdown for optional words
        this.children.each(function (itemView) {
          if (itemView.model.get("isOptional")) {
            itemView.$el.find(".dropdown-toggle").addClass("disabled");
          } else {
            itemView.$el.find(".dropdown-toggle").removeClass("disabled");
          }
        });
      } else {
        this.$el.find("#subButton").removeClass("active");
      }
    },
    _setTextArea: function () {
      const $textarea = this.$el.find("textarea");
      this.collection.each(function (model) {
        const text = model.get("surfaceForm");
        if (model.get("sequenceOrder") > 0) {
          $textarea.append(" ");
        }
        $textarea.append(text);
      }, this);
      this.text = this.getTextArea(); //save starting text in text variable
    },
    removeExtraWhitespace: function () {
      //call only when "done" button is clicked on
      let text = this.getTextArea();
      if (text === this.text) {
        //no change
        return;
      }
      //gets cursor position after change
      let cursorPos = this.getCursorPosition();
      //remove line breaks that appear despite blocking their entry (could come from a paste)
      const strippedText = this.processWhitespace(text);
      const lineBreaksRemovedBeforeCursor = text.length - strippedText.length;
      if (lineBreaksRemovedBeforeCursor > 0) {
        //all line breaks must always be before the cursor
        console.log("removed " + lineBreaksRemovedBeforeCursor + " line breaks!");
        cursorPos = cursorPos - lineBreaksRemovedBeforeCursor;
        text = strippedText; //get the updated text
      }
      this.trigger("text:changed", text, cursorPos);
      this.text = text; //update text length
      //no need to change the text area, since done button was clicked. Whitespace is to
      //be removed in the background after done is clicked on.
    },
    updateText: function () {
      const text = this.getTextArea();
      if (text === this.text) {
        //no change
        return;
      }
      //gets cursor position after change
      const cursorPos = this.getCursorPosition();
      //trigger change event to update the example strength
      this.trigger("text:changed", text, cursorPos);
      this.text = text;
    },
  });

  return ExampleEditorView;
});
